<template>
    <div class="supsis-table">
        <div class="supsis-scroll" :class="{ 'has-groups': hasGroups }" ref="scrollContainer">
            <div class="supsis-table-header">
                <div v-for="(col, index) in visibleColumnDefs" :key="col.key" class="supsis-col"
                    :class="['supsis-col-' + col.key, { 'supsis-col-fixed': col.fixed }]" :style="getColStyle(col.key)">
                    <div class="supsis-header-cell" :class="{
                        'supsis-resizing-active': resizing.active && resizing.colKey === col.key,
                        'supsis-dragging': dragging.active && dragging.dragIndex === index,
                        'supsis-drag-over': dragging.active && dragging.hoverIndex === index,
                        'supsis-sortable': sortableColumns && col.selectable !== false,
                        'supsis-has-sort': col.sortable
                    }" :draggable="sortableColumns && col.selectable !== false" @dragstart="onDragStart($event, index)"
                        @dragenter="onDragEnter($event, index)" @dragover="onDragOver($event, index)"
                        @dragleave="onDragLeave($event, index)" @drop="onDrop($event, index)"
                        @dragend="onDragEnd($event)"
                        @contextmenu.prevent="onHeaderContextMenu($event, col, index)">
                        <div class="supsis-header-content" @click="onHeaderClick($event, col)">
                            <template v-if="col.key === '__select'">
                                <input type="checkbox" class="select-all-checkbox" :checked="isAllSelected"
                                    :indeterminate.prop="isIndeterminate" @click.stop="onSelectAll" />
                            </template>
                            <template v-else>
                                <i v-if="sortableColumns && col.selectable !== false"
                                    class="material-icons drag-handle">drag_indicator</i>
                                <span class="supsis-header-title">{{ col.title }}</span>
                                <i v-if="col.sortable" class="material-icons sort-icon" :class="{
                                    'sort-active': sortState.key === col.key,
                                    'sort-asc': sortState.key === col.key && sortState.order === 'asc',
                                    'sort-desc': sortState.key === col.key && sortState.order === 'desc'
                                }">
                                    {{ sortState.key === col.key ? (sortState.order === 'asc' ? 'arrow_upward' :
                                        'arrow_downward') : 'unfold_more' }}
                                </i>
                            </template>
                        </div>
                        <span v-if="resizable && isColumnResizable(col)" class="supsis-resizer"
                            @mousedown="startResize($event, col.key, index)"></span>
                    </div>
                </div>
                
                <!-- Column Manager Button -->
                <div v-if="showColumnAdd" class="supsis-column-manager-container">
                    <MinimalPopoverSelect 
                        :value="visibleColumnKeys" 
                        :options="columnSelectOptions" 
                        :multiple="true"
                        :enableSearch="true" 
                        :show-icon="false" 
                        :show-dropdown-arrow="false"
                        :button-class="'supsis-column-manager-btn'" 
                        :popover-class="'supsis-column-manager-popover'"
                        :search-placeholder="'Kolon ara...'" 
                        @input="onColumnMultiInput"
                    >
                        <template v-slot:trigger>
                            <button class="supsis-column-manager-btn" type="button" v-tippy="{ content: 'Kolonları Yönet', placement: 'left' }">
                                <i class="material-icons">more_vert</i>
                            </button>
                        </template>
                    </MinimalPopoverSelect>
                </div>
            </div>

            <!-- Grouped Virtual Scroll View -->
            <div v-if="hasGroups" class="supsis-table-body supsis-virtual-scroll" ref="virtualScrollBody">
                <div class="supsis-virtual-container"
                    :style="{ height: totalHeight + 'px', position: 'relative', width: 'max-content', minWidth: '100%' }">

                    <!-- Render by Groups -->
                    <div v-for="groupSection in visibleGroupSections" :key="groupSection.groupKey"
                        class="supsis-group-section" :style="{
                            position: 'absolute',
                            top: groupSection.top + 'px',
                            left: 0,
                            width: 'max-content',
                            minWidth: '100%',
                            height: groupSection.height + 'px'
                        }">

                        <!-- Sticky Group Header -->
                        <div class="supsis-group-header" :style="{
                            position: 'sticky',
                            top: 0,
                            left: 0,
                            zIndex: 8
                        }" @click="toggleGroup(groupSection.groupKey)">
                            <i class="material-icons expand-icon">
                                {{ expandedGroups[groupSection.groupKey] !== false ? 'expand_more' : 'chevron_right' }}
                            </i>
                            <div class="group-label-container">
                                <span class="group-label">{{ groupSection.group.label || groupSection.group.name ||
                                    groupSection.groupKey }}</span>
                                <span class="group-count">{{ groupSection.group.count || 0 }}</span>
                            </div>
                        </div>

                        <!-- Group Items -->
                        <div v-if="expandedGroups[groupSection.groupKey] !== false" class="supsis-group-items" :style="{
                            position: 'relative',
                            height: groupSection.itemsHeight + 'px'
                        }">
                            <div v-for="item in groupSection.visibleItems"
                                :key="'item-' + groupSection.groupKey + '-' + item.itemIndex" class="supsis-row" :style="{
                                    position: 'absolute',
                                    top: item.relativeTop + 'px',
                                    left: 0,
                                    width: 'max-content',
                                    minWidth: '100%',
                                    height: itemHeight + 'px'
                                }" @click="onRowClick(getRowData(item), item.itemIndex, $event)"
                                @dblclick="onRowDblClick(getRowData(item), item.itemIndex, $event)">
                                <template v-if="getRowData(item)">
                                    <div v-for="col in visibleColumnDefs" :key="col.key" class="supsis-col"
                                        :class="['supsis-col-' + col.key, { 'supsis-col-fixed': col.fixed }]"
                                        :style="getColStyle(col.key)">
                                        <div class="supsis-cell-content" :class="{ 'supsis-filterable-cell': filterable && col.filterable !== false && col.key !== '__select' }"
                                            @contextmenu.prevent="onCellContextMenu($event, getRowData(item), col, item.itemIndex)">
                                            <template v-if="col.key === '__select'">
                                                <input type="checkbox" class="row-select-checkbox"
                                                    :checked="isRowSelected(getRowData(item))"
                                                    :disabled="isRowDisabled(getRowData(item)) || (selectedRowKeys.length >= maxSelectCount && !isRowSelected(getRowData(item)))"
                                                    @click.stop="onRowSelect(getRowData(item), $event)" />
                                            </template>
                                            <template v-else>
                                                <div class="supsis-cell-wrapper">
                                                    <slot :name="'cell-' + col.key" :row="getRowData(item)"
                                                        :value="getMaskedCellValue(getRowData(item), col)" :column="col"
                                                        :rowIndex="item.itemIndex">
                                                        <span class="supsis-default-cell">{{ getMaskedCellValue(getRowData(item), col)
                                                            }}</span>
                                                    </slot>
                                                    <i v-if="filterable && col.filterable !== false"
                                                        class="material-icons supsis-filter-icon"
                                                        :class="{ 'supsis-filter-active': isFilterActive(getRowData(item), col.key) }"
                                                        @click.stop="onFilterToggle(col.key, getRowData(item)[col.key], getRowData(item), item.itemIndex)">
                                                        {{ isFilterActive(getRowData(item), col.key) ? 'remove_circle' : 'add_circle' }}
                                                    </i>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div v-for="col in visibleColumnDefs" :key="col.key"
                                        class="supsis-col supsis-skeleton-col" :class="['supsis-col-' + col.key]"
                                        :style="getColStyle(col.key)">
                                        <div class="supsis-cell-content">
                                            <template v-if="col.key === '__select'">
                                                <input type="checkbox" class="row-select-checkbox skeleton-checkbox"
                                                    disabled />
                                            </template>
                                            <template v-else>
                                                <div class="supsis-skeleton-line"></div>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Normal Non-Grouped View -->
            <div v-else class="supsis-table-body" :class="{ 'supsis-zebra': zebra }">
                <div v-for="(row, rowIndex) in data" :key="getRowKey(row, rowIndex)" class="supsis-row"
                    @click="onRowClick(row, rowIndex, $event)" @dblclick="onRowDblClick(row, rowIndex, $event)">
                    <div v-for="col in visibleColumnDefs" :key="col.key" class="supsis-col"
                        :class="['supsis-col-' + col.key, { 'supsis-col-fixed': col.fixed }]"
                        :style="getColStyle(col.key)">
                        <div class="supsis-cell-content" :class="{ 'supsis-filterable-cell': filterable && col.filterable !== false && col.key !== '__select' }"
                            @contextmenu.prevent="onCellContextMenu($event, row, col, rowIndex)">
                            <template v-if="col.key === '__select'">
                                <input type="checkbox" class="row-select-checkbox" :checked="isRowSelected(row)"
                                    :disabled="isRowDisabled(row) || (selectedRowKeys.length >= maxSelectCount && !isRowSelected(row))"
                                    @click.stop="onRowSelect(row, $event)" />
                            </template>
                            <template v-else>
                                <div class="supsis-cell-wrapper">
                                    <slot :name="'cell-' + col.key" :row="row" :value="getMaskedCellValue(row, col)" :column="col"
                                        :rowIndex="rowIndex">
                                        <span class="supsis-default-cell">{{ getMaskedCellValue(row, col) }}</span>
                                    </slot>
                                    <i v-if="filterable && col.filterable !== false"
                                        class="material-icons supsis-filter-icon"
                                        :class="{ 'supsis-filter-active': isFilterActive(row, col.key) }"
                                        @click.stop="onFilterToggle(col.key, row[col.key], row, rowIndex)">
                                        {{ isFilterActive(row, col.key) ? 'remove_circle' : 'add_circle' }}
                                    </i>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="!data || !data.length" class="supsis-empty">
                    <slot name="empty">
                        <span>Veri bulunamadı</span>
                    </slot>
                </div>
                <div v-if="loading" class="supsis-loading-overlay"></div>
            </div>
        </div>

        <!-- Cell Context Menu -->
        <ContextMenu
            :visible="cellContextMenu.visible"
            :x="cellContextMenu.x"
            :y="cellContextMenu.y"
            :items="cellContextMenuItems"
            @item-click="onCellContextMenuItemClick"
        />

        <!-- Header Context Menu -->
        <ContextMenu
            :visible="headerContextMenu.visible"
            :x="headerContextMenu.x"
            :y="headerContextMenu.y"
            :items="headerContextMenuItems"
            @item-click="onHeaderContextMenuItemClick"
        />
    </div>
</template>

<script>
import MinimalPopoverSelect from "../popup-chat/chat-window/MinimalPopoverSelect.vue";
import ContextMenu from "./ContextMenu.vue";
import _ from "lodash";
import { mapState } from "vuex";
import { isMaskContactInfo, maskContactInfo } from "../../utils/utils";

export default {
    name: "NewSupsisTable",
    props: {
        columns: {
            type: Array,
            default: function () { return []; }
        },
        data: {
            type: Array,
            default: function () { return []; }
        },
        zebra: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        rowKey: {
            type: [String, Function],
            default: "_id"
        },
        resizable: {
            type: Boolean,
            default: true
        },
        minColumnWidth: {
            type: Number,
            default: 80
        },
        showColumnAdd: {
            type: Boolean,
            default: true
        },
        columnAddButtonIcon: {
            type: String,
            default: "widgets"
        },
        sortableColumns: {
            type: Boolean,
            default: false
        },
        localStorageKey: {
            type: String,
            default: null
        },
        groups: {
            type: Array,
            default: function () { return []; }
        },
        scrollLoading: {
            type: Boolean,
            default: false
        },
        itemHeight: {
            type: Number,
            default: 48
        },
        groupHeaderHeight: {
            type: Number,
            default: 48
        },
        bufferSize: {
            type: Number,
            default: 5
        },
        externalScrollTop: {
            type: Number,
            default: 0
        },
        externalContainerHeight: {
            type: Number,
            default: 0
        },
        selectable: {
            type: Boolean,
            default: false
        },
        maxSelectCount: {
            type: Number,
            default: 500
        },
        filterable: {
            type: Boolean,
            default: false
        },
        filters: {
            type: Array,
            default: function () { return []; }
        },
        sortField: {
            type: String,
            default: null
        },
        sortDirection: {
            type: String,
            default: null
        }
    },
    data: function () {
        return {
            internalVisibleColumns: [],
            widthsByKey: {},
            internalColumnOrder: {}, // Column order state'ini burada tut
            sortState: {
                key: null,
                order: null // 'asc' veya 'desc'
            },
            resizing: {
                active: false,
                startX: 0,
                colKey: null,
                colIndex: -1,
                startWidth: 0
            },
            dragging: {
                active: false,
                dragIndex: -1,
                hoverIndex: -1,
                startX: 0,
                startY: 0
            },
            clickTimer: null,
            columnPickerOpen: false,
            expandedGroups: {},
            selectedRowKeys: [],
            cellContextMenu: {
                visible: false,
                x: 0,
                y: 0,
                row: null,
                column: null,
                value: null,
                rowIndex: -1
            },
            headerContextMenu: {
                visible: false,
                x: 0,
                y: 0,
                column: null,
                columnIndex: -1
            }
        };
    },
    computed: {
        ...mapState({
            setting: function (state) { return state.setting.settings; },
            user: function (state) { return state.auth.user; }
        }),
        visibleColumnKeys: function () {
            var self = this;
            var keys = this.internalVisibleColumns.slice();

            // Eğer order state'i varsa, order'a göre sırala
            var hasOrders = keys.some(function (key) {
                return self.internalColumnOrder[key] !== undefined;
            });

            if (hasOrders) {
                keys.sort(function (a, b) {
                    var orderA = self.internalColumnOrder[a] || 999999; // Order yoksa en sona
                    var orderB = self.internalColumnOrder[b] || 999999;
                    return orderA - orderB;
                });
            }

            return keys;
        },
        selectableColumns: function () {
            return this.allColumns.filter(function (c) { return c.selectable !== false; });
        },
        visibleColumnDefs: function () {
            var self = this;
            var keys = this.visibleColumnKeys;
            var mapped = [];

            // Aktif kolonları bul ve state'lerini ekle
            for (var i = 0; i < this.allColumns.length; i++) {
                var col = this.allColumns[i];
                var isAlways = col.selectable === false;
                if (isAlways || keys.indexOf(col.key) !== -1) {
                    // Column'u clone et ve internal state'lerini ekle
                    var enhancedCol = Object.assign({}, col);

                    // Width state'ini ekle
                    enhancedCol.computedWidth = self.widthsByKey[col.key] || col.width || self.minColumnWidth;

                    // Order state'ini ekle (ensureColumnOrder sayesinde her zaman var)
                    enhancedCol.computedOrder = self.internalColumnOrder[col.key] || 0;

                    mapped.push(enhancedCol);
                }
            }

            // Internal column order'a göre sırala
            mapped.sort(function (a, b) {
                return a.computedOrder - b.computedOrder;
            });

            return mapped;
        },
        hiddenColumns: function () {
            var self = this;
            var hidden = [];
            for (var i = 0; i < this.columns.length; i++) {
                var c = this.columns[i];
                if (c.selectable === false) continue;
                if (this.visibleColumnKeys.indexOf(c.key) === -1) hidden.push(c);
            }
            return hidden;
        },
        columnSelectOptions: function () {
            var opts = [];
            for (var i = 0; i < this.selectableColumns.length; i++) {
                var c = this.selectableColumns[i];
                opts.push({ label: c.title, value: c.key, icon: c.icon, group: c.group });
            }
            return opts;
        },
        hasGroups: function () {
            return this.groups && this.groups.length > 0;
        },
        effectiveScrollTop: function () {
            return this.externalScrollTop || 0;
        },
        effectiveContainerHeight: function () {
            return this.externalContainerHeight || 600;
        },
        allColumns: function () {
            var columns = this.columns.slice();

            if (this.selectable) {
                var selectColumn = {
                    key: '__select',
                    title: '',
                    width: 50,
                    minWidth: 50,
                    fixed: true,
                    fixedPosition: 'left',
                    selectable: false,
                    resizable: false,
                    sortable: false
                };
                columns.unshift(selectColumn);
            }

            return columns;
        },
        isAllSelected: function () {
            if (!this.selectable || this.data.length === 0) return false;
            var selectableRows = this.data.filter(function (row) { return row && !row.__disabled; });
            return selectableRows.length > 0 && selectableRows.every(function (row) {
                return this.selectedRowKeys.indexOf(this.getRowKey(row, -1)) !== -1;
            }.bind(this));
        },
        isIndeterminate: function () {
            if (!this.selectable || this.selectedRowKeys.length === 0) return false;
            return this.selectedRowKeys.length > 0 && !this.isAllSelected;
        },
        totalHeight: function () {
            if (!this.hasGroups) return 0;

            var currentTop = 0;
            for (var i = 0; i < this.groups.length; i++) {
                var group = this.groups[i];
                var groupKey = group.value || group.key || group.id;
                var isExpanded = this.expandedGroups[groupKey] !== false;
                var itemCount = group.count || 0;

                currentTop += this.groupHeaderHeight;

                if (isExpanded) {
                    currentTop += itemCount * this.itemHeight;
                }
            }

            return currentTop;
        },
        dataByGroup: function () {
            if (!this.hasGroups) return {};

            var grouped = {};
            for (var i = 0; i < this.data.length; i++) {
                var row = this.data[i];
                var groupKey = row.__groupKey;
                var groupIndex = row.__groupIndex;

                if (!groupKey) {
                    continue;
                }

                if (!grouped[groupKey]) {
                    grouped[groupKey] = [];
                }

                if (groupIndex !== undefined) {
                    grouped[groupKey][groupIndex] = row;
                } else {
                    grouped[groupKey].push(row);
                }
            }

            var summary = {};
            for (var gKey in grouped) {
                summary[gKey] = grouped[gKey].filter(function (item) { return item !== undefined; }).length;
            }

            return grouped;
        },
        visibleGroupSections: function () {
            if (!this.hasGroups) return [];

            var self = this;
            var sections = [];
            var currentTop = 0;

            var startY = this.effectiveScrollTop;
            var endY = this.effectiveScrollTop + this.effectiveContainerHeight;
            var bufferHeight = this.bufferSize * this.itemHeight;
            var visibleStartY = Math.max(0, startY - bufferHeight);
            var visibleEndY = endY + bufferHeight;

            for (var i = 0; i < this.groups.length; i++) {
                var group = this.groups[i];
                var groupKey = group.value || group.key || group.id;
                var isExpanded = this.expandedGroups[groupKey] !== false;
                var itemCount = group.count || 0;

                var sectionTop = currentTop;
                var sectionHeight = this.groupHeaderHeight;

                if (isExpanded) {
                    sectionHeight += itemCount * this.itemHeight;
                }

                var sectionBottom = sectionTop + sectionHeight;

                currentTop = sectionBottom;

                if (sectionBottom < visibleStartY || sectionTop > visibleEndY) {
                    continue;
                }

                var visibleItems = [];

                if (isExpanded) {
                    var itemsStartTop = sectionTop + this.groupHeaderHeight;

                    for (var j = 0; j < itemCount; j++) {
                        var itemAbsoluteTop = itemsStartTop + (j * this.itemHeight);
                        var itemAbsoluteBottom = itemAbsoluteTop + this.itemHeight;

                        if (itemAbsoluteBottom >= visibleStartY && itemAbsoluteTop <= visibleEndY) {
                            visibleItems.push({
                                itemIndex: j,
                                groupKey: groupKey,
                                relativeTop: j * this.itemHeight,
                                absoluteTop: itemAbsoluteTop
                            });
                        }
                    }
                }

                sections.push({
                    groupKey: groupKey,
                    group: group,
                    top: sectionTop,
                    height: sectionHeight,
                    itemsHeight: isExpanded ? itemCount * this.itemHeight : 0,
                    visibleItems: visibleItems,
                    isExpanded: isExpanded
                });
            }

            return sections;
        },
        cellContextMenuItems: function () {
            if (!this.filterable || !this.cellContextMenu.column) return [];

            var items = [
                {
                    icon: 'add_circle',
                    label: 'Filtreye Ekle',
                    action: 'add'
                }
            ];

            if (this.cellContextMenu.row && this.isFilterActive(this.cellContextMenu.row, this.cellContextMenu.column.key)) {
                items.push({
                    icon: 'remove_circle',
                    label: 'Filtreden Çıkar',
                    action: 'remove',
                    danger: true
                });
            }

            return items;
        },
        headerContextMenuItems: function () {
            if (!this.headerContextMenu.column) return [];

            var col = this.headerContextMenu.column;
            var index = this.headerContextMenu.columnIndex;
            var visibleCount = this.visibleColumnDefs.length;

            // İlk non-fixed kolon index'ini bul
            var firstNonFixedIndex = 0;
            for (var i = 0; i < this.visibleColumnDefs.length; i++) {
                if (!this.visibleColumnDefs[i].fixed) {
                    firstNonFixedIndex = i;
                    break;
                }
            }

            var items = [];

            // Gizle (sadece selectable kolonlar için)
            if (col.selectable !== false) {
                items.push({
                    icon: 'visibility_off',
                    label: 'Gizle',
                    action: 'hide'
                });
            }

            items.push({ divider: true });

            // Solundaki kolon fixed mi kontrol et
            var leftColumn = index > 0 ? this.visibleColumnDefs[index - 1] : null;
            var hasFixedLeft = leftColumn && leftColumn.fixed;

            // Sola kaydır
            items.push({
                icon: 'keyboard_arrow_left',
                label: 'Sola Kaydır',
                action: 'move-left',
                disabled: index === 0 || hasFixedLeft
            });

            // Sağa kaydır
            items.push({
                icon: 'keyboard_arrow_right',
                label: 'Sağa Kaydır',
                action: 'move-right',
                disabled: index === visibleCount - 1
            });

            items.push({ divider: true });

            // En başa at (fixed kolonları atla)
            items.push({
                icon: 'first_page',
                label: 'En Başa At',
                action: 'move-first',
                disabled: index === firstNonFixedIndex || col.fixed
            });

            // En sona at
            items.push({
                icon: 'last_page',
                label: 'En Sona At',
                action: 'move-last',
                disabled: index === visibleCount - 1
            });

            return items;
        }
    },
    watch: {
        columns: {
            handler: function () {
                this.ensureWidths();
                this.ensureVisibleColumns();
                this.ensureColumnOrder();
            },
            deep: true
        },
        selectable: function () {
            this.ensureWidths();
            this.ensureVisibleColumns();
            this.ensureColumnOrder();
        },
        groups: {
            handler: function (newGroups, oldGroups) {
                console.log('[NewSupsisTable] 👁️ groups changed from', (oldGroups || []).length, 'to', newGroups.length);

                var oldGroupKeys = (oldGroups || []).map(g => g.value || g.key || g.id);
                var newGroupKeys = newGroups.map(g => g.value || g.key || g.id);

                console.log('[NewSupsisTable] Group keys changed:', {
                    from: oldGroupKeys,
                    to: newGroupKeys,
                    isComplete: oldGroupKeys.length !== newGroupKeys.length ||
                        !oldGroupKeys.every(key => newGroupKeys.includes(key))
                });

                // ✅ Sadece yeni gruplar için expandedGroups oluştur
                var newExpandedGroups = {};
                for (var i = 0; i < newGroups.length; i++) {
                    var groupKey = newGroups[i].value || newGroups[i].key || newGroups[i].id;
                    newExpandedGroups[groupKey] = true;
                }
                this.expandedGroups = newExpandedGroups;

                console.log('[NewSupsisTable] 🔄 ✅ expandedGroups completely reset:', Object.keys(this.expandedGroups));

                this.$nextTick(function () {
                    this.checkAndLoadMore();
                });
            },
            deep: true
        },
        data: {
            handler: function () {
                console.log('[NewSupsisTable] 👁️ data changed:', {
                    length: this.data.length,
                    hasGroups: this.hasGroups,
                    groupKeys: this.data.filter(d => d.__groupKey).map(d => d.__groupKey),
                    uniqueGroupKeys: [...new Set(this.data.filter(d => d.__groupKey).map(d => d.__groupKey))]
                });

                this.$nextTick(function () {
                    this.checkAndLoadMore();
                });
            },
            deep: true
        },
        externalScrollTop: function (newVal) {
            this.checkAndLoadMore();
        },
        externalContainerHeight: function (newVal) {
            this.checkAndLoadMore();
        },
        sortField: {
            handler: function (newVal) {
                if (!newVal || !this.sortDirection) {
                    this.sortState.key = null;
                    this.sortState.order = null;
                } else {
                    this.sortState.key = newVal;
                    this.sortState.order = this.sortDirection.toLowerCase();
                }
            },
            immediate: true
        },
        sortDirection: {
            handler: function (newVal) {
                if (!newVal || !this.sortField) {
                    this.sortState.key = null;
                    this.sortState.order = null;
                } else {
                    this.sortState.key = this.sortField;
                    this.sortState.order = newVal.toLowerCase();
                }
            },
            immediate: true
        }
    },
    created: function () {
        this.loadTableStateFromStorage();

        this.ensureWidths();
        this.ensureVisibleColumns();
        this.ensureColumnOrder();

        if (typeof window !== "undefined") {
            window.addEventListener("mousemove", this.onMouseMove);
            window.addEventListener("mouseup", this.onMouseUp);
        }

        if (this.hasGroups) {
            for (var i = 0; i < this.groups.length; i++) {
                var groupKey = this.groups[i].value || this.groups[i].key || this.groups[i].id;
                this.$set(this.expandedGroups, groupKey, true);
            }
        }

    },
    mounted: function () {
        var self = this;

        if (typeof window !== "undefined") {
            window.addEventListener("mousemove", this.onMouseMove);
            window.addEventListener("mouseup", this.onMouseUp);
            window.addEventListener("click", this.closeAllContextMenus);
        }

        this.$nextTick(function () {
            if (self.hasGroups) {
                self.checkAndLoadMore();
            }
        });
    },
    beforeDestroy: function () {
        if (typeof window !== "undefined") {
            window.removeEventListener("mousemove", this.onMouseMove);
            window.removeEventListener("mouseup", this.onMouseUp);
            window.removeEventListener("click", this.closeAllContextMenus);
        }
        if (this.clickTimer) {
            clearTimeout(this.clickTimer);
            this.clickTimer = null;
        }
    },
    methods: {
        getMaskedCellValue: function (row, col) {
            if (!row) return "";
            const val = row[col.key];
            if (val === undefined || val === null) return val;
            if (col.masked !== false && isMaskContactInfo(col.key)) {
                return maskContactInfo(String(val));
            }
            // Herhangi bir kolondaki değer telefon numarası görünüyorsa maskele
            const maskedPhoneFields = ["email", "phone", "whatsappNumber"];
            if (col.masked !== false && maskedPhoneFields.indexOf(col.key) === -1 && typeof val === "string") {
                const phonePattern = /^[\+\d\s\-\(\)]{7,}$/;
                if (phonePattern.test(val.trim()) && isMaskContactInfo("phone")) {
                    return maskContactInfo(val.trim());
                }
            }
            return val;
        },
        ensureVisibleColumns: function () {
            // Eğer internal zaten doluysa (storage'dan yüklenmişse), dokunma
            if (this.internalVisibleColumns && this.internalVisibleColumns.length > 0) return;

            // Son çare: default visible column'ları kullan
            var defaults = [];
            for (var i = 0; i < this.allColumns.length; i++) {
                var c = this.allColumns[i];
                if (c.selectable === false) continue;
                if (c.visible) defaults.push(c.key);
            }
            this.internalVisibleColumns = defaults;
        },
        ensureWidths: function () {
            // Sadece eksik column width'lerini ekle, mevcut olanları preserve et
            for (var i = 0; i < this.allColumns.length; i++) {
                var c = this.allColumns[i];

                // Her kolon için kendi minWidth'ini kullan, yoksa global minColumnWidth kullan
                var columnMinWidth = c.minWidth !== undefined ? c.minWidth : this.minColumnWidth;

                // Eğer bu column için width yoksa, default width ata
                if (!this.widthsByKey[c.key]) {
                    var defaultWidth = c && c.width ? c.width : columnMinWidth;
                    if (defaultWidth < columnMinWidth) defaultWidth = columnMinWidth;

                    // Vue reactivity için $set kullan (ama burada plain object olduğu için direct assignment)
                    this.$set ?
                        this.$set(this.widthsByKey, c.key, defaultWidth) :
                        (this.widthsByKey[c.key] = defaultWidth);
                }
            }
        },
        ensureColumnOrder: function () {
            // Tüm visible columns için order state'ini guarantee et
            var keys = this.internalVisibleColumns.slice(); // Raw array sırası
            var hasAnyOrder = false;

            // Mevcut order'ların olup olmadığını kontrol et (SADECE mevcut kolonlar için)
            for (var i = 0; i < keys.length; i++) {
                if (this.internalColumnOrder[keys[i]] !== undefined) {
                    hasAnyOrder = true;
                    break;
                }
            }

            // Eğer hiç order yoksa (ilk kez), original array sırasını kullan
            if (!hasAnyOrder) {
                for (var j = 0; j < keys.length; j++) {
                    this.internalColumnOrder[keys[j]] = j * 10;
                }
            } else {
                // Sadece eksik olan yeni kolonları en sona ekle
                var maxOrder = Math.max.apply(Math, Object.values(this.internalColumnOrder));
                for (var k = 0; k < keys.length; k++) {
                    if (this.internalColumnOrder[keys[k]] === undefined) {
                        maxOrder += 10;
                        this.internalColumnOrder[keys[k]] = maxOrder;
                    }
                }
            }
        },
        getColStyle: function (key) {
            // visibleColumnDefs'den computed width'i al
            var column = this.visibleColumnDefs.find(function (col) { return col.key === key; });
            var width = column ? column.computedWidth : this.minColumnWidth;

            var style = {
                minWidth: width + 'px',
                maxWidth: width + 'px',
                width: width + 'px'
            };

            // Fixed kolon ise sticky position ekle
            if (column && column.fixed) {
                style.position = 'sticky';

                var fixedPosition = column.fixedPosition || 'left'; // Default: left

                // Sol fixed kolonlar sağ fixed kolonların üstünde olmalı
                style.zIndex = fixedPosition === 'left' ? 6 : 5;

                if (fixedPosition === 'left') {
                    // Soldan: Önceki fixed kolonların toplam genişliğini hesapla
                    var leftOffset = 0;
                    for (var i = 0; i < this.visibleColumnDefs.length; i++) {
                        var col = this.visibleColumnDefs[i];
                        if (col.key === key) break;
                        if (col.fixed && (col.fixedPosition || 'left') === 'left') {
                            leftOffset += col.computedWidth;
                        }
                    }
                    style.left = leftOffset + 'px';
                } else if (fixedPosition === 'right') {
                    // Sağdan: Sonraki fixed kolonların toplam genişliğini hesapla
                    var rightOffset = 0;
                    var foundCurrent = false;
                    for (var j = 0; j < this.visibleColumnDefs.length; j++) {
                        var col2 = this.visibleColumnDefs[j];
                        if (col2.key === key) {
                            foundCurrent = true;
                            continue;
                        }
                        if (foundCurrent && col2.fixed && col2.fixedPosition === 'right') {
                            rightOffset += col2.computedWidth;
                        }
                    }
                    style.right = rightOffset + 'px';
                }
            }

            return style;
        },
        isVisible: function (key) {
            return this.visibleColumnKeys.indexOf(key) !== -1 || this.isAlwaysVisible(key);
        },
        isAlwaysVisible: function (key) {
            for (var i = 0; i < this.allColumns.length; i++) {
                var c = this.allColumns[i];
                if (c.key === key) return c.selectable === false;
            }
            return false;
        },
        isColumnResizable: function (col) {
            if (col.key === '__select') return false;
            if (typeof col.resizable === "boolean") return col.resizable;
            return true;
        },
        getRowKey: function (row, rowIndex) {
            if (typeof this.rowKey === 'function') return this.rowKey(row, rowIndex);
            return row && row[this.rowKey] ? row[this.rowKey] : rowIndex;
        },
        startResize: function (event, colKey, colIndex) {
            event.preventDefault();
            var currentWidth = this.widthsByKey[colKey] || this.minColumnWidth;
            this.resizing.active = true;
            this.resizing.startX = event.clientX;
            this.resizing.colKey = colKey;
            this.resizing.colIndex = colIndex;
            this.resizing.startWidth = currentWidth;
        },
        onMouseMove: function (event) {
            if (!this.resizing.active) return;
            var delta = event.clientX - this.resizing.startX;
            var newWidth = this.resizing.startWidth + delta;

            // Her kolon için kendi minWidth'ini kullan, yoksa global minColumnWidth kullan
            var column = this.allColumns.find(function (col) { return col.key === this.resizing.colKey; }.bind(this));
            var columnMinWidth = column && column.minWidth ? column.minWidth : this.minColumnWidth;

            if (newWidth < columnMinWidth) newWidth = columnMinWidth;
            var map = {};
            for (var k in this.widthsByKey) { map[k] = this.widthsByKey[k]; }
            map[this.resizing.colKey] = newWidth;
            this.widthsByKey = map;
        },
        onMouseUp: function () {
            if (!this.resizing.active) return;
            this.resizing.active = false;
            this.resizing.colKey = null;
            this.resizing.colIndex = -1;

            // Resize bittiğinde state'i kaydet
            this.saveTableStateToStorage();
        },
        onRowClick: function (row, rowIndex, event) {
            var self = this;
            if (this.clickTimer) {
                clearTimeout(this.clickTimer);
                this.clickTimer = null;
            }
            this.clickTimer = setTimeout(function () {
                self.$emit('row-click', row, rowIndex, event);
                self.clickTimer = null;
            }, 220);
        },
        onRowDblClick: function (row, rowIndex, event) {
            if (this.clickTimer) {
                clearTimeout(this.clickTimer);
                this.clickTimer = null;
            }
            this.$emit('row-dblclick', row, rowIndex, event);
        },
        onToggleColumn: function (key, checked) {
            var current = this.visibleColumnKeys.slice();
            var idx = current.indexOf(key);
            if (checked && idx === -1) current.push(key);
            if (!checked && idx !== -1) current.splice(idx, 1);
            this.internalVisibleColumns = current;

            // Hemen order'ı güncelle ki UI doğru sıralamayı göstersin
            this.ensureColumnOrder();
            this.saveTableStateToStorage();
        },
        onColumnMultiInput: function (newKeys) {
            this.internalVisibleColumns = Array.isArray(newKeys) ? newKeys : [];

            // Hemen order'ı güncelle ki UI doğru sıralamayı göstersin
            this.ensureColumnOrder();
            this.saveTableStateToStorage();
        },
        toggleColumnPicker: function () {
            this.columnPickerOpen = !this.columnPickerOpen;
        },
        onDragStart: function (event, index) {
            if (!this.sortableColumns) return;

            this.dragging.active = true;
            this.dragging.dragIndex = index;
            this.dragging.startX = event.clientX;
            this.dragging.startY = event.clientY;

            // Drag image'ı ayarla
            var dragImage = event.target.cloneNode(true);
            dragImage.style.opacity = '0.8';
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index.toString());
        },
        onDragEnter: function (event, index) {
            if (!this.dragging.active || this.dragging.dragIndex === index) return;
            event.preventDefault();
            this.dragging.hoverIndex = index;
        },
        onDragOver: function (event, index) {
            if (!this.dragging.active) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        },
        onDragLeave: function (event, index) {
            // Eğer drag leave gerçekten element'ten çıkış ise hover'ı temizle
            var rect = event.target.getBoundingClientRect();
            var x = event.clientX;
            var y = event.clientY;

            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                if (this.dragging.hoverIndex === index) {
                    this.dragging.hoverIndex = -1;
                }
            }
        },
        onDrop: function (event, dropIndex) {
            if (!this.dragging.active) return;
            event.preventDefault();

            var dragIndex = this.dragging.dragIndex;
            if (dragIndex !== dropIndex && dragIndex !== -1) {
                this.moveColumn(dragIndex, dropIndex);
            }

            this.resetDragging();
        },
        onDragEnd: function (event) {
            this.resetDragging();
        },
        resetDragging: function () {
            this.dragging.active = false;
            this.dragging.dragIndex = -1;
            this.dragging.hoverIndex = -1;
            this.dragging.startX = 0;
            this.dragging.startY = 0;
        },
        moveColumn: function (fromIndex, toIndex) {
            var visibleCols = this.visibleColumnDefs.slice();

            if (fromIndex < 0 || fromIndex >= visibleCols.length ||
                toIndex < 0 || toIndex >= visibleCols.length ||
                fromIndex === toIndex) {
                return;
            }

            // Sıralama işlemi için geçici array
            var orderedKeys = visibleCols.map(function (col) { return col.key; });

            // Element'i taşı
            var draggedKey = orderedKeys[fromIndex];
            orderedKeys.splice(fromIndex, 1);
            orderedKeys.splice(toIndex, 0, draggedKey);

            // Yeni order map'i oluştur - sadece taşınan kolonlar için değil TÜM kolonlar için
            var newOrderMap = Object.assign({}, this.internalColumnOrder);
            for (var i = 0; i < orderedKeys.length; i++) {
                newOrderMap[orderedKeys[i]] = i * 10;
            }

            this.internalColumnOrder = newOrderMap;
            this.saveTableStateToStorage();

            // Parent'a bilgi ver
            this.$emit('column-order-changed', orderedKeys, newOrderMap);
        },
        onHeaderClick: function (event, col) {
            // Eğer resize handle'a tıklanmışsa sort yapma
            if (event.target.classList.contains('supsis-resizer')) {
                return;
            }

            // Eğer drag handle'a tıklanmışsa sort yapma
            if (event.target.classList.contains('drag-handle')) {
                return;
            }

            // Select checkbox'ına tıklanmışsa sort yapma
            if (event.target.classList.contains('select-all-checkbox')) {
                return;
            }

            // Select column'ında ise sort yapma
            if (col.key === '__select') {
                return;
            }

            // Sadece sortable kolonlar için sort yap
            if (!col.sortable) {
                return;
            }

            this.toggleSort(col.key);
        },
        toggleSort: function (columnKey) {
            // Eğer aynı kolona tıklanmışsa order'ı değiştir
            if (this.sortState.key === columnKey) {
                if (this.sortState.order === 'asc') {
                    this.sortState.order = 'desc';
                } else if (this.sortState.order === 'desc') {
                    // Sort'u kaldır
                    this.sortState.key = null;
                    this.sortState.order = null;
                }
            } else {
                // Farklı bir kolona tıklanmışsa yeni sort başlat (asc ile)
                this.sortState.key = columnKey;
                this.sortState.order = 'asc';
            }

            // Parent'a sort değişikliğini bildir
            this.$emit('sort-change', {
                key: this.sortState.key,
                order: this.sortState.order
            });
        },
        loadTableStateFromStorage: function () {
            if (!this.localStorageKey) return;

            try {
                var savedData = this.getStorageData();

                // Column order'ı yükle
                if (savedData.orderMap) {
                    this.internalColumnOrder = savedData.orderMap;
                }

                // Width'leri yükle
                if (savedData.widthsMap) {
                    var mergedWidths = Object.assign({}, this.widthsByKey);
                    for (var key in savedData.widthsMap) {
                        var savedWidth = savedData.widthsMap[key];
                        if (savedWidth && savedWidth >= this.minColumnWidth) {
                            mergedWidths[key] = savedWidth;
                        }
                    }
                    this.widthsByKey = mergedWidths;
                }

                // Visible columns'ı yükle
                if (savedData.visibleColumns && Array.isArray(savedData.visibleColumns) && savedData.visibleColumns.length > 0) {
                    this.internalVisibleColumns = savedData.visibleColumns.slice();
                }
            } catch (error) {
                console.warn('Error loading table state from localStorage:', error);
            }
        },
        saveTableStateToStorage: function () {
            if (!this.localStorageKey) return;

            try {
                var tableState = {
                    orderMap: this.internalColumnOrder,
                    widthsMap: this.widthsByKey,
                    visibleColumns: this.internalVisibleColumns.slice(),
                    lastModified: Date.now()
                };

                localStorage.setItem(this.localStorageKey, JSON.stringify(tableState));
            } catch (error) {
                console.warn('Error saving table state to localStorage:', error);
            }
        },
        getStorageData: function () {
            try {
                var savedData = localStorage.getItem(this.localStorageKey);
                if (savedData) {
                    return JSON.parse(savedData);
                }
            } catch (error) {
                console.warn('Error parsing storage data:', error);
            }

            // Default storage structure
            return {
                orderMap: {},
                widthsMap: {},
                visibleColumns: [],
                lastModified: Date.now()
            };
        },
        toggleGroup: function (groupKey) {
            var currentState = this.expandedGroups[groupKey];
            var newState = currentState === false ? true : false;
            this.$set(this.expandedGroups, groupKey, newState);

            this.$nextTick(function () {
                this.checkAndLoadMore();
            });
        },
        checkAndLoadMore: function () {
            if (!this.hasGroups) {
                return;
            }

            if (!this.scrollLoading) {
                return;
            }

            var sections = this.visibleGroupSections;

            if (sections.length === 0) {
                return;
            }

            var missingDataFound = false;

            for (var i = 0; i < sections.length; i++) {
                var section = sections[i];

                if (!section.isExpanded) {
                    continue;
                }

                var groupKey = section.groupKey;
                var visibleItems = section.visibleItems;

                for (var j = 0; j < visibleItems.length; j++) {
                    var item = visibleItems[j];
                    var itemIndex = item.itemIndex;
                    var groupData = this.dataByGroup[groupKey] || [];
                    var hasData = !!groupData[itemIndex];

                    if (!hasData) {
                        var group = section.group;

                        if (group) {
                            var payload = {
                                groupKey: groupKey,
                                group: group,
                                offset: itemIndex,
                                limit: Math.min(20, (group.count || 0) - itemIndex)
                            };

                            this.$emit('scroll-load', payload);
                            missingDataFound = true;
                        }
                        break;
                    }
                }

                if (missingDataFound) {
                    break;
                }
            }
        },
        getRowData: function (item) {
            if (!item || item.itemIndex === undefined || !item.groupKey) return null;
            var groupData = this.dataByGroup[item.groupKey] || [];
            var rowData = groupData[item.itemIndex] || null;
            return rowData;
        },
        isRowSelected: function (row) {
            if (!this.selectable || !row) return false;
            var key = this.getRowKey(row, -1);
            return this.selectedRowKeys.indexOf(key) !== -1;
        },
        isRowDisabled: function (row) {
            return row && row.__disabled === true;
        },
        onSelectAll: function () {
            if (!this.selectable) return;

            var selectableRows = this.data.filter(function (row) {
                return row && !this.isRowDisabled(row);
            }.bind(this));

            if (this.isAllSelected) {
                this.selectedRowKeys = [];
            } else {
                var newSelection = [];
                for (var i = 0; i < Math.min(selectableRows.length, this.maxSelectCount); i++) {
                    var key = this.getRowKey(selectableRows[i], -1);
                    newSelection.push(key);
                }
                this.selectedRowKeys = newSelection;
            }

            this.emitSelectionChange();
        },
        onRowSelect: function (row, event) {
            if (!this.selectable || !row || this.isRowDisabled(row)) return;

            var key = this.getRowKey(row, -1);
            var index = this.selectedRowKeys.indexOf(key);

            if (index !== -1) {
                this.selectedRowKeys.splice(index, 1);
            } else {
                if (this.selectedRowKeys.length < this.maxSelectCount) {
                    this.selectedRowKeys.push(key);
                }
            }

            this.emitSelectionChange();
        },
        emitSelectionChange: function () {
            if (!this.selectable) return;

            var selectedData = [];
            for (var i = 0; i < this.selectedRowKeys.length; i++) {
                var key = this.selectedRowKeys[i];
                var row = this.data.find(function (r) {
                    return this.getRowKey(r, -1) === key;
                }.bind(this));
                if (row) selectedData.push(row);
            }

            this.$emit('selection-change', {
                selectedRowKeys: this.selectedRowKeys.slice(),
                selectedData: selectedData,
                count: this.selectedRowKeys.length
            });
        },
        clearSelection: function () {
            this.selectedRowKeys = [];
            this.emitSelectionChange();
        },
        onCellContextMenu: function (event, row, column, rowIndex) {
            if (!this.filterable || !column || column.filterable === false || column.key === '__select') {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            this.cellContextMenu.visible = true;
            this.cellContextMenu.x = event.clientX;
            this.cellContextMenu.y = event.clientY;
            this.cellContextMenu.row = row;
            this.cellContextMenu.column = column;
            this.cellContextMenu.value = row ? row[column.key] : null;
            this.cellContextMenu.rowIndex = rowIndex;

            // Header context menu'yu kapat
            this.headerContextMenu.visible = false;
        },
        onCellContextMenuItemClick: function (item) {
            if (!this.cellContextMenu.row || !this.cellContextMenu.column) {
                this.closeCellContextMenu();
                return;
            }

            const recordId = this.getRowKey(this.cellContextMenu.row, this.cellContextMenu.rowIndex);
            const column = this.cellContextMenu.column;

            this.$emit('filter-toggle', {
                action: item.action,
                recordId: recordId,
                field: column.key,
                value: this.cellContextMenu.value,
                row: this.cellContextMenu.row,
                isMultiple: column.multiple || false
            });

            this.closeCellContextMenu();
        },
        closeCellContextMenu: function () {
            this.cellContextMenu.visible = false;
            this.cellContextMenu.row = null;
            this.cellContextMenu.column = null;
            this.cellContextMenu.value = null;
            this.cellContextMenu.rowIndex = -1;
        },
        onHeaderContextMenu: function (event, column, columnIndex) {
            // Select ve Fixed kolonlarda context menu çıkmasın
            if (column.key === '__select' || column.fixed) return;

            event.preventDefault();
            event.stopPropagation();

            this.headerContextMenu.visible = true;
            this.headerContextMenu.x = event.clientX;
            this.headerContextMenu.y = event.clientY;
            this.headerContextMenu.column = column;
            this.headerContextMenu.columnIndex = columnIndex;

            // Cell context menu'yu kapat
            this.cellContextMenu.visible = false;
        },
        onHeaderContextMenuItemClick: function (item) {
            if (!this.headerContextMenu.column || item.disabled) {
                this.closeHeaderContextMenu();
                return;
            }

            var col = this.headerContextMenu.column;
            var index = this.headerContextMenu.columnIndex;

            switch (item.action) {
                case 'hide':
                    this.hideColumn(col.key);
                    break;
                case 'move-left':
                    if (index > 0) {
                        this.moveColumn(index, index - 1);
                    }
                    break;
                case 'move-right':
                    if (index < this.visibleColumnDefs.length - 1) {
                        this.moveColumn(index, index + 1);
                    }
                    break;
                case 'move-first':
                    if (index > 0 && !col.fixed) {
                        // İlk non-fixed pozisyonu bul
                        var firstNonFixedIndex = 0;
                        for (var i = 0; i < this.visibleColumnDefs.length; i++) {
                            if (!this.visibleColumnDefs[i].fixed) {
                                firstNonFixedIndex = i;
                                break;
                            }
                        }
                        this.moveColumn(index, firstNonFixedIndex);
                    }
                    break;
                case 'move-last':
                    if (index < this.visibleColumnDefs.length - 1) {
                        this.moveColumn(index, this.visibleColumnDefs.length - 1);
                    }
                    break;
            }

            this.closeHeaderContextMenu();
        },
        closeHeaderContextMenu: function () {
            this.headerContextMenu.visible = false;
            this.headerContextMenu.column = null;
            this.headerContextMenu.columnIndex = -1;
        },
        hideColumn: function (columnKey) {
            var current = this.visibleColumnKeys.slice();
            var idx = current.indexOf(columnKey);
            if (idx !== -1) {
                current.splice(idx, 1);
                this.internalVisibleColumns = current;
                this.saveTableStateToStorage();
            }
        },
        closeAllContextMenus: function () {
            this.closeCellContextMenu();
            this.closeHeaderContextMenu();
        },
        isFilterActive: function (row, columnKey) {
            if (!row || !this.filters || this.filters.length === 0) return false;
            
            var rawRowValue = row[columnKey];
            var column = this.getColumnByKey(columnKey);
            
            // Row value'yu normalize et (boş ise emptyValue kullan)
            var normalizedRowValue = this.normalizeRowValue(rawRowValue, column);

            for (var i = 0; i < this.filters.length; i++) {
                var filter = this.filters[i];
                
                if (filter.field !== columnKey) continue;
                
                // Filter value array ise
                if (Array.isArray(filter.value)) {
                    // Normalized row value array ise
                    if (Array.isArray(normalizedRowValue)) {
                        for (var j = 0; j < normalizedRowValue.length; j++) {
                            if (filter.value.indexOf(normalizedRowValue[j]) !== -1) {
                                return true;
                            }
                        }
                    } else {
                        // Normalized row value basit string ise
                        if (filter.value.indexOf(normalizedRowValue) !== -1) {
                            return true;
                        }
                    }
                } else {
                    // Filter value basit değer ise
                    if (Array.isArray(normalizedRowValue)) {
                        if (normalizedRowValue.indexOf(filter.value) !== -1) {
                            return true;
                        }
                    } else {
                        if (filter.value === normalizedRowValue) {
                            return true;
                        }
                    }
                }
            }
            
            return false;
        },
        getColumnByKey: function (key) {
            for (var i = 0; i < this.allColumns.length; i++) {
                if (this.allColumns[i].key === key) {
                    return this.allColumns[i];
                }
            }
            return null;
        },
        normalizeRowValue: function (value, column) {
            // Boş değer kontrolü
            if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                // Column'da emptyValue tanımlıysa onu kullan
                if (column && column.emptyValue) {
                    return column.emptyValue;
                }
                return value;
            }
            return value;
        },
        onFilterToggle: function (columnKey, value, row, rowIndex) {
            const recordId = this.getRowKey(row, rowIndex);
            const isActive = this.isFilterActive(row, columnKey);
            const column = this.getColumnByKey(columnKey);

            this.$emit('filter-toggle', {
                action: isActive ? 'remove' : 'add',
                recordId: recordId,
                field: columnKey,
                value: value,
                row: row,
                isMultiple: column ? column.multiple : false
            });
        }
    },
    components: { MinimalPopoverSelect, ContextMenu }
}
</script>

<style lang="scss" scoped>
.supsis-table {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;

    .supsis-scroll {
        flex: 1;
        overflow: auto;
        min-height: 0;
        display: flex;
        flex-direction: column;

        &.has-groups {
            overflow-y: hidden;
            overflow-x: auto;
        }
    }

    .supsis-table-header {
        display: flex;
        gap: 0;
        padding: 0;
        background: #f8f9fa;
        border-radius: 0;
        border-bottom: 2px solid #e0e0e0;
        min-width: max-content;
        flex-shrink: 0;
        z-index: 10;
        position: sticky;
        top: 0;
        
        .supsis-column-manager-container {
            position: sticky;
            right: 0;
            display: flex;
        align-items: center;
            background: #f8f9fa;
            padding: 0 8px;
            border-left: 1px solid #e0e0e0;
            z-index: 11;

        .supsis-column-manager-btn {
            display: inline-flex;
            align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                padding: 0;
                border: none;
                border-radius: 6px;
                background: transparent;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

            .material-icons {
                    font-size: 20px;
                color: #6b7785;
                transition: color 0.2s ease;
            }

            &:hover {
                    background: #e8ebed;

                .material-icons {
                    color: #22272d;
                }
            }

            &:active {
                    transform: scale(0.95);
                    background: #d9dce0;
                }
            }
        }

        .supsis-col {
            font-size: 12px;
            font-weight: 600;
            color: #6b7785;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            position: relative;

            // Fixed header kolonları için arka plan
            &.supsis-col-fixed {
                background-color: #f8f9fa !important;
            }

            .supsis-header-cell {
                width: 100%;
                display: flex;
                align-items: stretch;
                position: relative;

                .supsis-header-content {
                    padding: 12px 16px;
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    overflow: hidden;
                    position: relative;

                    .drag-handle {
                        position: absolute;
                        left: 0;
                        font-size: 16px;
                        color: #bfbfbf;
                        cursor: grab;
                        opacity: 0;
                        transition: opacity 0.2s ease;
                    }
                }

                &:hover .supsis-header-content .drag-handle {
                    opacity: 1;
                }

                .supsis-header-title {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .sort-icon {
                    font-size: 16px;
                    color: #bfbfbf;
                    margin-left: auto;
                    transition: color 0.2s ease;

                    &.sort-active {
                        color: #22272d;
                    }
                }

                .supsis-resizer {
                    position: absolute;
                    top: 0;
                    right: -4px;
                    width: 8px;
                    height: 100%;
                    cursor: col-resize;
                    background: transparent;
                    opacity: 0;
                    transition: background .12s ease, opacity .12s ease;

                    &:hover {
                        background: #e8e8e8;
                        opacity: 1;
                    }
                }

                &:hover .supsis-resizer {
                    opacity: 1;
                }

                &.supsis-resizing-active .supsis-resizer {
                    background: #22272d;
                    opacity: 1;
                }

                // Sortable column için cursor
                &.supsis-has-sort {
                    .supsis-header-content {
                        cursor: pointer;
                        user-select: none;

                        &:hover .sort-icon {
                            color: #6b7785;
                        }
                    }
                }

                // Drag & Drop States
                &.supsis-sortable {
                    cursor: grab;

                    &:active {
                        cursor: grabbing;
                    }
                }

                &.supsis-dragging {
                    opacity: 0.5;
                    transform: scale(0.95);
                    cursor: grabbing;
                    z-index: 1000;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

                    .drag-handle {
                        color: #22272d;
                    }
                }

                &.supsis-drag-over {
                    background: #13151917;
                    border-left: 3px solid #22272d;
                    transform: translateX(3px);

                    .supsis-header-content {
                        padding-left: 13px; // 16px - 3px border
                    }
                }
            }
        }
    }

    .supsis-table-body {
        min-width: max-content;
        position: relative;

        .supsis-row {
            display: flex;
            gap: 0;
            border-bottom: 1px solid #f0f0f0;
            cursor: pointer;
            background: #ffffff;
            width: max-content;
            min-width: 100%;

            &:hover {
                background: #e8ebed !important;
            }

            &:last-child {
                border-bottom: none;
            }
        }

        &.supsis-zebra .supsis-row:nth-child(even) {
            background: #fafafa;

            &:hover {
                background: #e8ebed !important;
            }
        }

        .supsis-col {
            display: flex;
            align-items: stretch;
            overflow: hidden;

            &.supsis-col-fixed {
                background-color: inherit;
            }
        }

        .supsis-cell-content {
            padding: 8px 16px;
            width: 100%;
            display: flex;
            align-items: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            position: relative;

            &.supsis-filterable-cell {
                padding-right: 32px;

                .supsis-cell-wrapper {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                    position: relative;

                    .supsis-filter-icon {
                        position: absolute;
                        right: 0;
                        top: 50%;
                        transform: translateY(-50%);
                        font-size: 16px;
                        color: #6b7785;
                        cursor: pointer;
                        opacity: 0;
                        transition: opacity 0.2s ease, color 0.2s ease;
                        flex-shrink: 0;
                        background: #fff;
                        padding: 4px;
                        border-radius: 4px;
                        z-index: 2;

                        &:hover {
                            color: #22272d;
                        }

                        &.supsis-filter-active {
                            color: #46499D;
                            opacity: 1;

                            &:hover {
                                color: #d32f2f;
                            }
                        }
                    }
                }

                &:hover .supsis-filter-icon {
                    opacity: 1;
                }
            }
        }

        .supsis-default-cell {
            color: #22272d;
            font-size: 14px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .supsis-empty {
            padding: 24px;
            color: #6b7785;
            text-align: center;
        }

        .supsis-loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.5);
            z-index: 6;
            overflow: hidden;
            pointer-events: all;
        }

        .supsis-loading-overlay::after {
            content: "";
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg,
                    rgba(255, 255, 255, 0) 0%,
                    rgba(255, 255, 255, 0.5) 50%,
                    rgba(255, 255, 255, 0) 100%);
            animation: supsis-shimmer 1.5s infinite;
        }

        &.supsis-virtual-scroll {
            flex: 1;
            min-height: 0;
            overflow: visible;
            -webkit-overflow-scrolling: touch;

            .supsis-virtual-container {
                position: relative;
                width: max-content;
                min-width: 100%;
                min-height: 100%;
            }

            .supsis-virtual-item {
                will-change: transform;
                width: max-content;
                min-width: 100%;
            }
        }
    }

    .supsis-group-section {
        width: max-content;
        min-width: 100%;
        overflow: visible;
    }

    .supsis-group-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 16px;
        background: #fff;
        border-bottom: 1px solid #f0f2f5;
        cursor: pointer;
        transition: background 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        user-select: none;
        width: 100%;
        min-width: max-content;

        &:hover {
            background: #fafbfc;

            .expand-icon {
                color: #495057;
            }
        }

        .expand-icon {
            font-size: 20px;
            color: #d0d7de;
            transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
            flex-shrink: 0;
        }

        .group-label-container {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
        }

        .group-label {
            font-size: 14px;
            font-weight: 600;
            color: #57606a;
            transition: all 0.18s ease;
            letter-spacing: -0.02em;
        }

        .group-count {
            font-size: 11px;
            font-weight: 600;
            color: #6c757d;
            background: #f1f3f5;
            padding: 2px 8px;
            border-radius: 12px;
            min-width: 22px;
            text-align: center;
            transition: all 0.18s ease;
            line-height: 1.4;
        }
    }

    .supsis-group-items {
        width: 100%;
        min-width: 100%;
    }

    .supsis-skeleton-col {
        .supsis-cell-content {
            padding: 12px 16px;
        }

        .supsis-skeleton-line {
            height: 12px;
            background: linear-gradient(90deg,
                    #f0f0f0 0%,
                    #e0e0e0 50%,
                    #f0f0f0 100%);
            background-size: 200% 100%;
            border-radius: 4px;
            animation: skeleton-shimmer 1.5s ease-in-out infinite;
            width: 80%;
        }

        .skeleton-checkbox {
            opacity: 0.3;
        }
    }

    .supsis-col-__select {
        .supsis-cell-content {
            justify-content: center;
            padding: 8px 12px;
        }

        .select-all-checkbox,
        .row-select-checkbox {
            width: 16px;
            height: 16px;
            cursor: pointer;
            accent-color: #46499D;

            &:disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }
        }

        .select-all-checkbox {
            &:indeterminate {
                accent-color: #46499D;
            }
        }
    }
}

@keyframes supsis-shimmer {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

@keyframes skeleton-shimmer {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}
</style>

<style lang="scss">
/* Global popover override for supsis column picker */
.supsis-column-manager-popover {
    .ant-popover-arrow {
        display: none;
    }

    .ant-popover-inner {
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.08);
    }

    .ant-popover-inner-content {
        border: 1px solid #e8e8e8;
        border-radius: 12px;
        padding: 0;
        overflow: hidden;
    }

    .supsis-column-list {
        min-width: 280px;
        max-height: 420px;
        overflow-y: auto;
        overflow-x: hidden;

        /* Custom scrollbar */
        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: #f8f9fa;
        }

        &::-webkit-scrollbar-thumb {
            background: #d0d7de;
            border-radius: 4px;

            &:hover {
                background: #b0b7be;
            }
        }
    }

    .supsis-column-search {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
        background: #fafbfc;

        input {
            width: 100%;
            padding: 8px 12px;
            border: 1.5px solid #d9d9d9;
            border-radius: 8px;
            font-size: 13px;
            outline: none;
            transition: all 0.2s ease;

            &:focus {
                border-color: #22272d;
                box-shadow: 0 0 0 3px rgba(34, 39, 45, 0.08);
            }

            &::placeholder {
                color: #9ca3af;
            }
        }
    }

    .supsis-column-list-inner {
        padding: 8px;
    }

    .supsis-column-group {
        margin-bottom: 4px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .supsis-column-group-title {
        padding: 8px 12px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6b7785;
        background: transparent;
    }

    .supsis-column-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        margin-bottom: 2px;

        input[type="checkbox"] {
            width: 16px;
            height: 16px;
            cursor: pointer;
            accent-color: #22272d;
            margin: 0;
            flex-shrink: 0;
        }

        .supsis-column-item-content {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;
        }

        .supsis-column-item-icon {
            font-size: 18px;
            color: #6b7785;
            flex-shrink: 0;
        }

        .supsis-column-item-title {
            font-size: 14px;
            font-weight: 500;
            color: #22272d;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
        }

        &:hover {
            background: #f1f3f5;

            .supsis-column-item-icon {
                color: #22272d;
            }
        }

        &.supsis-column-item-checked {
            background: #f8f9fa;

            .supsis-column-item-title {
                font-weight: 600;
                color: #22272d;
            }
        }
    }

    .supsis-column-list-footer {
        padding: 12px 16px;
        border-top: 1px solid #f0f0f0;
        background: #fafbfc;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;

        .footer-info {
            font-size: 12px;
            color: #6b7785;
            font-weight: 500;

            .count-highlight {
                font-weight: 700;
                color: #22272d;
            }
        }

        .footer-actions {
            display: flex;
            gap: 6px;

            button {
                padding: 6px 12px;
                border: 1px solid #d9d9d9;
                border-radius: 6px;
                background: #fff;
                font-size: 12px;
                font-weight: 600;
                color: #22272d;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover {
                    background: #f8f9fa;
                    border-color: #22272d;
                }

                &.reset-btn {
                    color: #6b7785;

                    &:hover {
                        color: #22272d;
                    }
                }
            }
        }
    }

    .supsis-column-empty {
        padding: 32px 16px;
        text-align: center;
        color: #9ca3af;
        font-size: 13px;

        .material-icons {
            font-size: 32px;
            margin-bottom: 8px;
            opacity: 0.5;
        }
    }
}
</style>

